import { Injectable } from '@angular/core';

export interface UtmCoords {
  easting: number;
  northing: number;
  zone: string;
}

@Injectable({ providedIn: 'root' })
export class ProjectionService {
  private static readonly A = 6378137;
  private static readonly F = 1 / 298.257223563;
  private static readonly E2 = 2 * ProjectionService.F - ProjectionService.F * ProjectionService.F;
  private static readonly E12 = ProjectionService.E2 / (1 - ProjectionService.E2);
  private static readonly K0 = 0.9996;

  private readonly zones: { zone: string; centralMeridian: number }[] = [
    { zone: '17S', centralMeridian: -81 },
    { zone: '18S', centralMeridian: -75 },
    { zone: '19S', centralMeridian: -69 },
  ];

  /** Detecta la zona UTM según la longitud (hemisferio sur para Perú: 17S–19S). */
  detectUtmZone(lon: number): string {
    const zone = Math.floor((lon + 180) / 6) + 1;
    return `${zone}S`;
  }

  toWgs84(lat: number, lon: number): string {
    return `${lat.toFixed(6)}°, ${lon.toFixed(6)}°`;
  }

  toUtm(lat: number, lon: number, zone: string): string {
    const cm = this.zones.find(z => z.zone === zone)?.centralMeridian;
    if (cm === undefined) return '';
    const c = this.computeUtm(lat, lon, cm);
    const ns = lat < 0 ? c.northing + 10000000 : c.northing;
    return `${c.easting.toFixed(2)} ${ns.toFixed(2)}`;
  }

  getAllUtm(lat: number, lon: number): UtmCoords[] {
    return this.zones.map(z => {
      const c = this.computeUtm(lat, lon, z.centralMeridian);
      const ns = lat < 0 ? c.northing + 10000000 : c.northing;
      return { zone: z.zone, easting: Math.round(c.easting * 100) / 100, northing: Math.round(ns * 100) / 100 };
    });
  }

  private computeUtm(lat: number, lon: number, centralMeridian: number): { easting: number; northing: number } {
    const φ = lat * Math.PI / 180;
    const λ = lon * Math.PI / 180;
    const λ0 = centralMeridian * Math.PI / 180;

    const sinφ = Math.sin(φ);
    const cosφ = Math.cos(φ);
    const tanφ = Math.tan(φ);
    const N = ProjectionService.A / Math.sqrt(1 - ProjectionService.E2 * sinφ * sinφ);
    const T = tanφ * tanφ;
    const C = ProjectionService.E12 * cosφ * cosφ;
    const A = (λ - λ0) * cosφ;

    const A2 = A * A;
    const A3 = A2 * A;
    const A4 = A3 * A;
    const A5 = A4 * A;
    const A6 = A5 * A;

    const e2 = ProjectionService.E2;
    const M = ProjectionService.A * (
      (1 - e2 / 4 - 3 * e2 * e2 / 64 - 5 * e2 * e2 * e2 / 256) * φ
      - (3 * e2 / 8 + 3 * e2 * e2 / 32 + 45 * e2 * e2 * e2 / 1024) * Math.sin(2 * φ)
      + (15 * e2 * e2 / 256 + 45 * e2 * e2 * e2 / 1024) * Math.sin(4 * φ)
      - (35 * e2 * e2 * e2 / 3072) * Math.sin(6 * φ)
    );

    const easting = 500000 + ProjectionService.K0 * N * (
      A + (1 - T + C) * A3 / 6 + (5 - 18 * T + T * T + 72 * C - 58 * e2) * A5 / 120
    );

    const northing = ProjectionService.K0 * (
      M + N * tanφ * (
        A2 / 2 + (5 - T + 9 * C + 4 * C * C) * A4 / 24 + (61 - 58 * T + T * T + 600 * C - 330 * e2) * A6 / 720
      )
    );

    return { easting, northing };
  }
}
